import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Dropdown
} from "react-bootstrap";
import CommonTable from '../components/CommonTable';
import '../tasks/tasklist.css';
import {
  FiFile,
  FiSearch,
  FiDownload,
  FiMoreVertical,
  FiRefreshCw,
  FiFolder,
  FiStar,
  FiTrash2,
  FiChevronDown,
  FiChevronRight
} from 'react-icons/fi';
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileImage,
  FaFileArchive,
  FaFileAlt,
  FaFolder
} from 'react-icons/fa';
import './documentDrive.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import { formatDateTimeToDDMMMYYYY } from '../../utils/dateFormatter';
import axiosInstance from '../../services/AxiosInstance';
import { getSavedViewMode, saveViewMode } from '../../utils/viewPersistence';
import { useDocumentAccess } from '../../hooks/useDocumentAccess';
import CategorizationIcon from './CategorizationIcon';

const FavoriteDocuments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess, loading: accessLoading } = useDocumentAccess();

  const [favoriteFiles, setFavoriteFiles] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [filteredGroupedData, setFilteredGroupedData] = useState([]);
  const [viewMode, setViewMode] = useState(() => getSavedViewMode('favoriteDocuments', 'table'));
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    hasMore: false
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const mobileMenuRef = useRef(null);
  const tableWrapperRef = useRef(null);

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
  });

  const notifyError = (msg) => toast.error(`❌ ${msg}`, {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });

  // Handle dropdown toggle to check position
  const handleDropdownToggle = (isOpen, event) => {
    if (!isOpen) return; // Only handle when opening

    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      try {
        // Find the currently open dropdown menu - check both container classes
        const visibleDropdowns = document.querySelectorAll('.user-list-card .dropdown-menu.show, .document-drive-container .dropdown-menu.show');

        if (visibleDropdowns.length === 0) {
          return; // No open dropdown found
        }

        // Get the most recently opened dropdown (last one in the list)
        const dropdownMenu = visibleDropdowns[visibleDropdowns.length - 1];
        const dropdown = dropdownMenu.closest('.dropdown');

        if (!dropdown) {
          return;
        }

        // Find the toggle button
        const toggleElement = dropdown.querySelector('.dropdown-toggle');

        if (!toggleElement) {
          return; // Can't determine position without toggle element
        }

        const rect = toggleElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Estimate dropdown menu height (typically 150-200px for 3-4 items)
        // Get actual menu height if available
        let estimatedDropdownHeight = 180;
        if (dropdownMenu.offsetHeight > 0) {
          estimatedDropdownHeight = dropdownMenu.offsetHeight;
        }

        // Be VERY conservative - only open above if there's clearly not enough space below
        // and significantly more space above
        const minSpaceBelow = estimatedDropdownHeight + 50; // Need at least 230px below (more conservative)
        const minSpaceAbove = estimatedDropdownHeight + 20; // Need at least 200px above

        // Only open above if:
        // 1. There's definitely not enough space below (less than minSpaceBelow)
        // 2. AND there's significantly more space above (at least 80px more - very conservative)
        // 3. AND there's enough space above to fit the menu
        const shouldOpenAbove = spaceBelow < minSpaceBelow &&
          spaceAbove > (spaceBelow + 80) &&
          spaceAbove >= minSpaceAbove;

        // Add/remove dropup class
        // Add class to card and column to allow dropdown overflow
        const driveItem = dropdown.closest('.drive-item');
        const driveItemCol = dropdown.closest('.drive-item-col');
        if (driveItem) driveItem.classList.add('dropdown-open');
        if (driveItemCol) driveItemCol.classList.add('dropdown-open');

        if (shouldOpenAbove) {
          dropdown.classList.add('dropup');
          // Check if menu gets cut off after Bootstrap positions it
          setTimeout(() => {
            const menuRect = dropdownMenu.getBoundingClientRect();
            // If menu is cut off at top, adjust max-height
            if (menuRect.top < 10) {
              const availableSpace = Math.max(spaceAbove - 10, 120);
              dropdownMenu.style.maxHeight = `${availableSpace}px`;
              dropdownMenu.style.overflowY = 'auto';
            } else {
              // Menu is fully visible, but ensure it doesn't exceed viewport
              const maxHeight = Math.min(menuRect.height, spaceAbove - 10);
              if (maxHeight < menuRect.height) {
                dropdownMenu.style.maxHeight = `${maxHeight}px`;
                dropdownMenu.style.overflowY = 'auto';
              }
            }
          }, 100);
        } else {
          dropdown.classList.remove('dropup');
          // Reset styles for dropdown opening below
          setTimeout(() => {
            dropdownMenu.style.maxHeight = '';
            dropdownMenu.style.overflowY = '';
            // Check if menu goes below viewport
            const menuRect = dropdownMenu.getBoundingClientRect();
            if (menuRect.bottom > viewportHeight - 10) {
              const availableSpace = Math.max(spaceBelow - 10, 120);
              dropdownMenu.style.maxHeight = `${availableSpace}px`;
              dropdownMenu.style.overflowY = 'auto';
            }
          }, 100);
        }

        // Remove the class when dropdown closes
        const removeOverflowClass = () => {
          if (driveItem) driveItem.classList.remove('dropdown-open');
          if (driveItemCol) driveItemCol.classList.remove('dropdown-open');
        };
        dropdown.addEventListener('hidden.bs.dropdown', removeOverflowClass, { once: true });
      } catch (error) {
        console.error('Error in handleDropdownToggle:', error);
      }
    }, 10); // Small delay to ensure DOM is updated
  };

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadFavoriteDocuments(1);
  }, []);

  useEffect(() => {
    filterFiles();
  }, [searchTerm, favoriteFiles, groupedData]);

  const formatDocumentItem = (item) => {
    if (!item) {
      return null;
    }

    if (item.document) {
      const formatted = formatDocumentItem(item.document);
      if (!formatted) {
        return null;
      }

      return {
        ...formatted,
        favorited_at: item.favorited_at || item.created_at || formatted.favorited_at
      };
    }

    const normalizedType = item.type || item.item_type || (item.is_folder ? 'folder' : 'file');

    if (normalizedType === 'file') {
      const fileName = item.name || item.file_name || '';
      const fileExt = item.file_type?.toLowerCase() || fileName.split('.').pop()?.toLowerCase() || '';

      const fileTypeMap = {
        'pdf': 'pdf',
        'doc': 'word',
        'docx': 'word',
        'xls': 'excel',
        'xlsx': 'excel',
        'ppt': 'powerpoint',
        'pptx': 'powerpoint',
        'jpg': 'image',
        'jpeg': 'image',
        'png': 'image',
        'gif': 'image'
      };

      return {
        id: item.id,
        name: fileName,
        file_name: fileName,
        type: 'file',
        folder_id: item.document_path_id || item.folder_id,
        folder_name: item.folder?.name || item.folder_name || 'My Documents',
        size: item.file_size ? `${(parseFloat(item.file_size) / 1024 / 1024).toFixed(2)} MB` : item.size || '0 MB',
        last_accessed: item.last_accessed || item.updated_at || item.created_at,
        last_modified: item.last_modified || item.updated_at || item.created_at,
        file_type: fileTypeMap[fileExt] || item.file_type || 'file',
        category_id: item.category?.id || item.category_id || null,
        category_name: item.category?.name || item.category_name || null,
        is_private: item.is_private,
        version: item.version || null,
        favorited_at: item.favorited_at || item.created_at || null,
        reference_number: item.reference_number || item.document_reference_number || null,
        expiry_date: item.expiry_date || item.document_expiry_date || null,
        document_title: item.document_title || null,
        uploaded_by_name: item.uploaded_by_user?.full_name || item.created_by_name || null,
        uploaded_at: item.uploaded_at || item.created_at || null,
      };
    }

    if (normalizedType === 'folder') {
      return {
        id: item.id,
        name: item.name,
        type: 'folder',
        parent_id: item.parent_id,
        folder_name: item.parent?.name ? `${item.parent.name} / ${item.name}` : (item.folder_name || item.name),
        last_accessed: item.last_accessed || item.updated_at || item.created_at,
        last_modified: item.last_modified || item.updated_at || item.created_at,
        file_type: 'folder',
        favorited_at: item.favorited_at || item.created_at || null
      };
    }

    return null;
  };

  const loadFavoriteDocuments = async (page = 1, append = false) => {
    // Prevent multiple simultaneous calls
    if (loading || loadingMore) return;
    
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;

      // Build query parameters
      const params = new URLSearchParams();
      params.append('corporate_id', corporateId);
      
      // Add pagination parameters
      params.append('page', page);
      params.append('limit', pagination.limit);

      const response = await axiosInstance.get(`/documents/favorites?${params.toString()}`);
      const data = response.data;

      if (response.status === 200 && data.success) {
        const responseData = data.data;

        // Store pagination information
        if (responseData.pagination) {
          setPagination(responseData.pagination);
        }

        // Check if data is grouped by category
        if (Array.isArray(responseData) && responseData.length > 0 && responseData[0].category !== undefined) {
          // New grouped structure
          const formattedGroups = responseData.map(group => ({
            category: group.category,
            documents: (group.documents || [])
              .map(doc => formatDocumentItem(doc))
              .filter(Boolean)
          }));

          setGroupedData(formattedGroups);

          // Flatten for backward compatibility with existing filtering logic
          const allDocuments = formattedGroups.flatMap(group => group.documents);
          
          if (append) {
            // Append new documents when loading more pages
            setFavoriteFiles(prev => [...prev, ...allDocuments]);
            // For grouped data, we need to merge groups - this is complex, so for now just replace
            setGroupedData(formattedGroups);
          } else {
            setFavoriteFiles(allDocuments);
            setGroupedData(formattedGroups);
          }
        } else {
          // Legacy flat structure (fallback)
          const items = responseData.items || responseData.documents || responseData || [];
          const formattedItems = items
            .map(formatDocumentItem)
            .filter(Boolean);

          if (append) {
            setFavoriteFiles(prev => [...prev, ...formattedItems]);
          } else {
            setFavoriteFiles(formattedItems);
            setGroupedData([]);
          }
        }
      } else if (response.status === 200) {
        // Handle case where API returns data directly without success wrapper
        const items = Array.isArray(data) ? data : (data.items || data.documents || []);
        const formattedItems = items
          .map(formatDocumentItem)
          .filter(Boolean);

        setFavoriteFiles(formattedItems);
        setGroupedData([]);
      }
    } catch (error) {
      console.error('Error fetching favorite documents:', error);
      notifyError(error.response?.data?.message || 'Failed to load favorite documents');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const filterFiles = () => {
    // If we have grouped data, filter it while maintaining the structure
    if (groupedData.length > 0) {
      const filteredGroups = groupedData.map(group => {
        let filteredDocs = [...group.documents];

        // Filter by search term
        if (searchTerm.trim()) {
          filteredDocs = filteredDocs.filter(file =>
            file.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            file.folder_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (group.category && group.category.name?.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        }

        return {
          category: group.category,
          documents: filteredDocs
        };
      }).filter(group => group.documents.length > 0); // Remove empty groups

      setFilteredGroupedData(filteredGroups);

      // Flatten for backward compatibility
      const allFiltered = filteredGroups.flatMap(group => group.documents);
      setFilteredFiles(allFiltered);
    } else {
      // Legacy flat structure filtering
      let filtered = [...favoriteFiles];

      // Filter by search term
      if (searchTerm.trim()) {
        filtered = filtered.filter(file =>
          file.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          file.folder_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setFilteredFiles(filtered);
      setFilteredGroupedData([]);
    }
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    saveViewMode('favoriteDocuments', mode);
  };

  // Handle category expand/collapse
  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  // Get category key for tracking expanded state
  const getCategoryKey = (category) => {
    return category ? `category-${category.id}` : 'Unclassified';
  };

  // Expand all categories by default when data loads
  useEffect(() => {
    if (filteredGroupedData.length > 0) {
      const allKeys = filteredGroupedData.map(group => getCategoryKey(group.category));
      const currentKeys = Array.from(expandedCategories);
      const keysChanged = allKeys.length !== currentKeys.length ||
        !allKeys.every(key => currentKeys.includes(key));

      if (keysChanged) {
        setExpandedCategories(new Set(allKeys));
      }
    }
  }, [filteredGroupedData]);

  const getClassificationIcon = (classificationName) => {
    if (!classificationName) return null;

    const normalizedName = classificationName.toLowerCase().trim();

    // Map classification names to icon types
    const classificationMap = {
      'contracts': 'word',
      'contract': 'word',
      'legal': 'word',
      'reports': 'pdf',
      'report': 'pdf',
      'invoices': 'excel',
      'invoice': 'excel',
      'financial': 'excel',
      'finance': 'excel',
      'hr documents': 'word',
      'hr': 'word',
      'human resources': 'word',
      'project documents': 'word',
      'project': 'word',
      'general': 'file',
      'images': 'image',
      'image': 'image',
      'photos': 'image',
      'photo': 'image',
      'presentations': 'powerpoint',
      'presentation': 'powerpoint',
      'slides': 'powerpoint',
      'spreadsheets': 'excel',
      'spreadsheet': 'excel',
      'documents': 'word',
      'document': 'word',
      'archives': 'archive',
      'archive': 'archive',
      'zip': 'archive'
    };

    return classificationMap[normalizedName] || null;
  };

  const getFileIcon = (file) => {
    // Get file extension from file_name first (most reliable)
    let extension = '';
    if (file.file_name) {
      extension = file.file_name.split('.').pop()?.toLowerCase() || '';
    } else if (file.name) {
      extension = file.name.split('.').pop()?.toLowerCase() || '';
    }
    
    // If we have file_type, check if it's a MIME type or extension
    let fileType = file.file_type?.toLowerCase() || '';
    
    if (fileType) {
      // Check if it's a MIME type (contains '/')
      if (fileType.includes('/')) {
        // Extract extension from MIME type
        const mimeToExt = {
          'application/pdf': 'pdf',
          'application/msword': 'doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'application/vnd.ms-excel': 'xls',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'application/vnd.ms-powerpoint': 'ppt',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
          'image/jpeg': 'jpg',
          'image/jpg': 'jpg',
          'image/png': 'png',
          'image/gif': 'gif',
          'text/plain': 'txt',
          'application/zip': 'zip',
          'application/x-rar-compressed': 'rar',
          'application/x-zip-compressed': 'zip'
        };
        extension = mimeToExt[fileType] || extension;
      } else {
        // It's already an extension
        if (fileType[0] === '.') {
          fileType = fileType.substring(1);
        }
        extension = fileType || extension;
      }
    }
    
    // Use extension if we have it, otherwise fallback
    const fileTypeKey = extension || fileType || '';
    
    const iconMap = {
      pdf: 'pdf',
      doc: 'word',
      docx: 'word',
      xls: 'excel',
      xlsx: 'excel',
      ppt: 'powerpoint',
      pptx: 'powerpoint',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
      gif: 'image',
      txt: 'text',
      zip: 'archive',
      rar: 'archive',
      folder: 'folder'
    };

    return iconMap[fileTypeKey] || 'file';
  };

  const getFileIconColor = (fileType) => {
    const colorMap = {
      pdf: '#d93025',
      word: '#2b579a',
      excel: '#1d6f42',
      powerpoint: '#d04423',
      image: '#34a853',
      text: '#6c757d',
      archive: '#ea4335',
      file: '#667eea'
    };
    return colorMap[fileType] || colorMap.file;
  };

  const getClassificationIconComponent = (classificationName, size = 20, isFolder = false) => {
    const defaultColor = '#FFB900';
    
    if (!classificationName) {
      return <CategorizationIcon size={size} color={defaultColor} />;
    }

    const iconType = getClassificationIcon(classificationName);

    switch (iconType) {
      case 'pdf':
        return <FaFilePdf size={size} style={{ color: '#d93025' }} />;
      case 'word':
        return <FaFileWord size={size} style={{ color: '#2b579a' }} />;
      case 'excel':
        return <FaFileExcel size={size} style={{ color: '#1d6f42' }} />;
      case 'powerpoint':
        return <FaFilePowerpoint size={size} style={{ color: '#d04423' }} />;
      case 'image':
        return <FaFileImage size={size} style={{ color: '#34a853' }} />;
      case 'archive':
        return <FaFileArchive size={size} style={{ color: '#ea4335' }} />;
      case 'text':
        return <FaFileAlt size={size} style={{ color: '#6c757d' }} />;
      default:
        return <CategorizationIcon size={size} color={defaultColor} />;
    }
  };

  const getFileIconComponent = (file, size = 20, iconColor = null) => {
    if (file.type === 'folder') {
      // For folders, try to use classification if available
      const classificationName = file.category_name || file.category?.name;
      return getClassificationIconComponent(classificationName, size, true);
    }

    const normalizedType = getFileIcon(file);
    const color = iconColor || getFileIconColor(normalizedType);

    switch (normalizedType) {
      case 'pdf':
        return <FaFilePdf size={size} style={{ color }} />;
      case 'word':
        return <FaFileWord size={size} style={{ color }} />;
      case 'excel':
        return <FaFileExcel size={size} style={{ color }} />;
      case 'powerpoint':
        return <FaFilePowerpoint size={size} style={{ color }} />;
      case 'image':
        return <FaFileImage size={size} style={{ color }} />;
      case 'archive':
        return <FaFileArchive size={size} style={{ color }} />;
      case 'text':
        return <FaFileAlt size={size} style={{ color }} />;
      default:
        return <FiFile size={size} style={{ color }} />;
    }
  };

  const handleViewDetails = (item) => {
    if (item.type === 'file' && item.id) {
      navigate(`/documents/details/${item.id}`, {
        state: { from: location.pathname }
      });
    }
  };

  const handleNavigateToFile = (item) => {
    if (item.type === 'folder') {
      if (item.id) {
        navigate(`/documents/folder/${item.id}`);
      } else {
        navigate('/documents');
      }
    } else {
      if (item.folder_id || item.document_path_id) {
        const folderId = item.folder_id || item.document_path_id;
        navigate(`/documents/folder/${folderId}`);
      } else {
        navigate('/documents');
      }
    }
    notifySuccess('Navigating to location...');
  };

  const handleDownload = async (file) => {
    if (!file || !file.id) {
      notifyError('Invalid file');
      return;
    }

    try {
      setLoading(true);

      const response = await axiosInstance.get(`/documents/${file.id}/download`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/octet-stream'
        }
      });

      if (response.status === 200) {
        let fileName = file.name || file.file_name || 'document';

        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
          }
        }

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        notifySuccess('File downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading file:', error);

      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          notifyError(errorJson.message || 'Failed to download file');
        } catch (parseError) {
          notifyError('Failed to download file');
        }
      } else {
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to download file';
        notifyError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (item) => {
    if (!item || !item.id) {
      notifyError('Invalid document');
      return;
    }

    try {
      setLoading(true);

      const response = await axiosInstance.put(`/documents/${item.id}/favorite`);

      if (response.status === 200 || response.status === 204) {
        notifySuccess('Document removed from favorites');
        // Refresh favorite documents
        setPagination(prev => ({ ...prev, page: 1 }));
        await loadFavoriteDocuments(1);
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to remove from favorites';
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Scroll-based pagination handler
  useEffect(() => {
    if (viewMode === 'table') {
      // For table view, use table wrapper scroll
      const handleTableScroll = (event) => {
        const threshold = 5;
        const wrapper = event.target;
        if (wrapper && wrapper.classList.contains('table-wrapper')) {
          const isNearBottom = wrapper.scrollHeight - wrapper.scrollTop <= wrapper.clientHeight + threshold;
          if (isNearBottom && pagination.hasMore && !loadingMore && !loading) {
            const nextPage = pagination.page + 1;
            setPagination(prev => ({ ...prev, page: nextPage }));
            loadFavoriteDocuments(nextPage, true);
          }
        }
      };

      const wrapper = document.querySelector('.table-wrapper');
      if (wrapper) {
        wrapper.addEventListener('scroll', handleTableScroll);
        return () => {
          wrapper.removeEventListener('scroll', handleTableScroll);
        };
      }
      return () => {};
    } else {
      // For tile/card view, use window scroll
      const handleWindowScroll = () => {
        const threshold = 5;
        const isNearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - threshold;
        if (isNearBottom && pagination.hasMore && !loadingMore && !loading) {
          const nextPage = pagination.page + 1;
          setPagination(prev => ({ ...prev, page: nextPage }));
          loadFavoriteDocuments(nextPage, true);
        }
      };

      window.addEventListener('scroll', handleWindowScroll);
      return () => {
        window.removeEventListener('scroll', handleWindowScroll);
      };
    }
  }, [pagination.hasMore, loadingMore, loading, pagination.page, viewMode, favoriteFiles, groupedData]);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);

      if (diffInSeconds < 60) return 'just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
      if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

      return formatDateTimeToDDMMMYYYY(dateString);
    } catch (error) {
      return dateString;
    }
  };


  // Define columns for CommonTable
  const columns = [
    {
      key: 'icon',
      header: '',
      render: (row) => {
        const fileIconType = getFileIcon(row);
        const iconColor = getFileIconColor(fileIconType);
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {getFileIconComponent(row, 20, iconColor)}
          </div>
        );
      },
      cellClassName: 'width80',
    },
    {
      key: 'title',
      header: 'Document Title',
      render: (row) => (
        <div
          onClick={() => {
            if (row.type === 'file') {
              handleViewDetails(row);
            } else {
              handleNavigateToFile(row);
            }
          }}
          style={{ cursor: 'pointer', color: '#333', fontWeight: '500' }}
          className="text-truncate"
          title={row.name}
        >
          {row.name}
        </div>
      ),
    },
    {
      key: 'reference',
      header: 'Document Reference Number',
      render: (row) => (
        <span className="text-muted">{row.document_reference_number || row.reference_number || '—'}</span>
      ),
    },
    {
      key: 'expiry',
      header: 'Document Expiry Date',
      render: (row) => (
        <span className="text-muted">{row.document_expiry_date || row.expiry_date || '—'}</span>
      ),
    },
    {
      key: 'uploadedDate',
      header: 'Uploaded Date',
      render: (row) => (
        <span className="text-muted">
          {row.created_at || row.favorited_at ? formatTimeAgo(row.created_at || row.favorited_at) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'uploadedBy',
      header: 'Uploaded by',
      render: (row) => (
        <span className="text-muted">
          {row.uploaded_by || row.created_by_name || '—'}
        </span>
      ),
    },
    {
      key: 'classification',
      header: 'Document Classification',
      render: (row) => (
        <span className="text-muted">{row.category_name || 'N/A'}</span>
      ),
    },
  ];

  // Define actions for CommonTable
  const getActions = (row, rowIndex) => {
    const actions = [];

    if (row.type === 'file') {
      actions.push({
        label: 'View Details',
        icon: 'fas fa-file',
        onClick: () => handleViewDetails(row),
      });
      actions.push({
        label: 'Download',
        icon: 'fas fa-download',
        onClick: () => handleDownload(row),
      });
    } else if (row.type === 'folder') {
      actions.push({
        label: 'Open Folder',
        icon: 'fas fa-folder-open',
        onClick: () => handleNavigateToFile(row),
      });
    }

    actions.push({
      label: 'Remove from Favorites',
      icon: 'fas fa-trash',
      className: 'text-danger',
      onClick: () => handleRemoveFavorite(row),
    });

    return actions;
  };

  // Show loading while checking access
  if (accessLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render if no access (will redirect)
  if (!hasAccess) {
    return null;
  }

  return (
    <>
      <ToastContainer />
      <Row>
        <Col lg={12}>
          <Card className="user-list-card">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0" style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2c3e50' }}>
                Favorite Documents
              </h5>
            </Card.Header>

            {/* Search and Filter Section */}
            <div className="document-search-filter-section" style={{ padding: '1rem 0', position: 'relative' }}>
              <div className="document-search-row" style={{ display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'space-between', padding: '0 1.8rem' }}>
                <div className="search-input-group" style={{ flex: 1 }}>
                  <FiSearch className="search-icon" />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search favorite documents and classifications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                {/* Mobile Menu Toggle Button */}
                <button
                  className="mobile-menu-toggle"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  aria-label="Toggle menu"
                >
                  <i className={`fas ${showMobileMenu ? 'fa-times' : 'fa-ellipsis-v'}`}></i>
                </button>
                {/* Desktop Controls - Hidden on Mobile */}
                <div className="desktop-controls" style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row', alignItems: 'center', gap: '1rem' }}>
                  <div className="view-toggle-container-modern">
                    <button
                      className={`view-toggle-btn ${viewMode === 'tile' ? 'active' : ''}`}
                      onClick={() => handleViewModeChange('tile')}
                      title="Tile View"
                    >
                      <i className="fas fa-th-large"></i>
                    </button>
                    <button
                      className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                      onClick={() => handleViewModeChange('table')}
                      title="Table View"
                    >
                      <i className="fas fa-list"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Collapsible Menu */}
              <div 
                ref={mobileMenuRef}
                className={`mobile-controls-menu ${showMobileMenu ? 'active' : ''}`}
              >
                <div className="mobile-controls-row">
                  {/* View Toggle */}
                  <div className="view-toggle-container-modern">
                    <button
                      className={`view-toggle-btn-modern ${viewMode === 'tile' ? 'active' : ''}`}
                      onClick={() => {
                        handleViewModeChange('tile');
                        setShowMobileMenu(false);
                      }}
                      title="Tile View"
                    >
                      <i className="fas fa-th-large"></i>
                    </button>
                    <button
                      className={`view-toggle-btn-modern ${viewMode === 'table' ? 'active' : ''}`}
                      onClick={() => {
                        handleViewModeChange('table');
                        setShowMobileMenu(false);
                      }}
                      title="Table View"
                    >
                      <i className="fas fa-list"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <Card.Body style={{ minHeight: '400px', overflow: 'visible' }}>
              {loading && favoriteFiles.length === 0 && groupedData.length === 0 ? (
                <div className="d-flex justify-content-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading favorite documents...</span>
                  </div>
                </div>
              ) : viewMode === 'tile' ? (
                <>
                  {filteredGroupedData.length > 0 ? (
                    filteredGroupedData.map((group, groupIndex) => {
                      const categoryKey = getCategoryKey(group.category);
                      const isExpanded = expandedCategories.has(categoryKey);

                      return (
                        <div key={groupIndex} style={{ marginBottom: '2rem' }}>
                          <div
                            onClick={() => toggleCategory(categoryKey)}
                            style={{
                              marginBottom: '1rem',
                              paddingBottom: '0.5rem',
                              borderBottom: '2px solid #e9ecef',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              cursor: 'pointer',
                              userSelect: 'none',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {isExpanded ? (
                              <FiChevronDown size={18} style={{ color: '#C42126' }} />
                            ) : (
                              <FiChevronRight size={18} style={{ color: '#C42126' }} />
                            )}
                            {getClassificationIconComponent(group.category?.name, 20, true)}
                            <h6 className="mb-0" style={{
                              color: '#C42126',
                              fontWeight: '600',
                              fontSize: '1.1rem',
                              flex: 1
                            }}>
                              {group.category ? group.category.name : 'Unclassified'}
                            </h6>
                            <span className="text-muted small" style={{ fontSize: '0.85rem' }}>
                              ({group.documents.length} {group.documents.length === 1 ? 'document' : 'documents'})
                            </span>
                          </div>
                          {isExpanded && (
                            <Row className="drive-grid">
                              {group.documents.map((item) => {
                                const fileIconType = getFileIcon(item);
                                const iconColor = getFileIconColor(fileIconType);
                                const isFolder = item.type === 'folder';

                                return (
                                  <Col key={item.id} xs={6} sm={4} md={3} lg={2} xl={2} className="drive-item-col">
                                    <Card
                                      className={`drive-item ${isFolder ? 'folder-item' : 'file-item'}`}
                                      onClick={(e) => {
                                        const target = e.target;
                                        if (target.closest('.drive-item-actions') ||
                                          target.closest('.dropdown') ||
                                          target.closest('.dropdown-toggle') ||
                                          target.closest('.dropdown-menu')) {
                                          return;
                                        }
                                        if (item.type === 'file') {
                                          handleViewDetails(item);
                                        } else {
                                          handleNavigateToFile(item);
                                        }
                                      }}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <div className="drive-item-icon" style={{ color: iconColor }}>
                                        {isFolder ? getFileIconComponent(item, 48) : getFileIconComponent(item, 48)}
                                      </div>
                                      <div className="drive-item-name" title={item.name}>
                                        {item.name}
                                      </div>
                                      {item.category_name && (
                                        <div className="text-muted small mt-1" style={{ fontSize: '0.75rem', color: '#C42126' }}>
                                          {item.category_name}
                                        </div>
                                      )}
                                      {item.favorited_at && (
                                        <div className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                                          Favorited {formatTimeAgo(item.favorited_at)}
                                        </div>
                                      )}
                                      <div className="drive-item-actions" onClick={(e) => e.stopPropagation()}>
                                        <Dropdown>
                                          <Dropdown.Toggle variant="link" className="drive-menu-toggle-docs" id={`dropdown-${item.id}`}>
                                            <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
                                              <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                <rect x="0" y="0" width="24" height="24"></rect>
                                                <circle fill="#000000" cx="5" cy="12" r="2"></circle>
                                                <circle fill="#000000" cx="12" cy="12" r="2"></circle>
                                                <circle fill="#000000" cx="19" cy="12" r="2"></circle>
                                              </g>
                                            </svg>
                                          </Dropdown.Toggle>
                                          <Dropdown.Menu>
                                            {item.type === 'file' && (
                                              <>
                                                <Dropdown.Item onClick={() => handleViewDetails(item)}>
                                                  <FiFile className="me-2" />
                                                  View Details
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => handleDownload(item)}>
                                                  <FiDownload className="me-2" />
                                                  Download
                                                </Dropdown.Item>
                                              </>
                                            )}
                                            {item.type === 'folder' && (
                                              <Dropdown.Item onClick={() => handleNavigateToFile(item)}>
                                                <FiFolder className="me-2" />
                                                Open Folder
                                              </Dropdown.Item>
                                            )}
                                            <Dropdown.Item
                                              onClick={() => handleRemoveFavorite(item)}
                                              className="text-danger"
                                            >
                                              <FiTrash2 className="me-2" />
                                              Remove from Favorites
                                            </Dropdown.Item>
                                          </Dropdown.Menu>
                                        </Dropdown>
                                      </div>
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>
                          )}
                        </div>
                      );
                    })
                  ) : filteredFiles.length > 0 ? (
                    <Row className="drive-grid">
                      {filteredFiles.map((item) => {
                        const fileIconType = getFileIcon(item);
                        const iconColor = getFileIconColor(fileIconType);
                        const isFolder = item.type === 'folder';

                        return (
                          <Col key={item.id} xs={6} sm={4} md={3} lg={2} xl={2} className="drive-item-col">
                            <Card
                              className={`drive-item ${isFolder ? 'folder-item' : 'file-item'}`}
                              onClick={(e) => {
                                const target = e.target;
                                if (target.closest('.drive-item-actions') ||
                                  target.closest('.dropdown') ||
                                  target.closest('.dropdown-toggle') ||
                                  target.closest('.dropdown-menu')) {
                                  return;
                                }
                                if (item.type === 'file') {
                                  handleViewDetails(item);
                                } else {
                                  handleNavigateToFile(item);
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="drive-item-icon" style={{ color: iconColor }}>
                                {isFolder ? <FaFolder size={48} /> : getFileIconComponent(item, 48)}
                              </div>
                              <div className="drive-item-name" title={item.name}>
                                {item.name}
                              </div>
                              {item.category_name && (
                                <div className="text-muted small mt-1" style={{ fontSize: '0.75rem', color: '#C42126' }}>
                                  {item.category_name}
                                </div>
                              )}
                              {item.favorited_at && (
                                <div className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                                  Favorited {formatTimeAgo(item.favorited_at)}
                                </div>
                              )}
                              <div className="drive-item-actions" onClick={(e) => e.stopPropagation()}>
                                <Dropdown
                                  onToggle={handleDropdownToggle}
                                >
                                  <Dropdown.Toggle variant="link" className="drive-menu-toggle-docs" id={`dropdown-${item.id}`}>
                                    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
                                      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                        <rect x="0" y="0" width="24" height="24"></rect>
                                        <circle fill="#000000" cx="5" cy="12" r="2"></circle>
                                        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
                                        <circle fill="#000000" cx="19" cy="12" r="2"></circle>
                                      </g>
                                    </svg>
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    {item.type === 'file' && (
                                      <>
                                        <Dropdown.Item onClick={() => handleViewDetails(item)}>
                                          <FiFile className="me-2" />
                                          View Details
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => handleDownload(item)}>
                                          <FiDownload className="me-2" />
                                          Download
                                        </Dropdown.Item>
                                      </>
                                    )}
                                    {item.type === 'folder' && (
                                      <Dropdown.Item onClick={() => handleNavigateToFile(item)}>
                                        <FiFolder className="me-2" />
                                        Open Folder
                                      </Dropdown.Item>
                                    )}
                                    <Dropdown.Item
                                      onClick={() => handleRemoveFavorite(item)}
                                      className="text-danger"
                                    >
                                      <FiTrash2 className="me-2" />
                                      Remove from Favorites
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  ) : (
                    <Col xs={12} className="text-center py-5">
                      <FiFolder size={48} className="text-muted mb-3" />
                      <p className="text-muted">
                        {searchTerm
                          ? 'No files match your search criteria'
                          : 'No favorite documents available'}
                      </p>
                    </Col>
                  )}
                </>
              ) : (
                <>
                  {filteredGroupedData.length > 0 ? (
                    <div className="col-12">
                      <div className="table-view-container">
                        <div className="table-wrapper" ref={tableWrapperRef}>
                          <table className="tasks-table">
                            <thead>
                              <tr>
                                <th className="width80"></th>
                                <th>Title</th>
                                <th>Classification</th>
                                <th>Reference Number</th>
                                <th>Expiry Date</th>
                                <th>Uploaded Date</th>
                                <th>Uploaded by</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredGroupedData.map((group, groupIndex) => {
                                const categoryKey = getCategoryKey(group.category);
                                const isExpanded = expandedCategories.has(categoryKey);

                                return (
                                  <React.Fragment key={groupIndex}>
                                    <tr
                                      onClick={() => toggleCategory(categoryKey)}
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        transition: 'background-color 0.2s'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                    >
                                      <td colSpan="8" style={{ padding: '0.75rem 1rem' }}>
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.5rem'
                                        }}>
                                          {isExpanded ? (
                                            <FiChevronDown size={18} style={{ color: '#C42126' }} />
                                          ) : (
                                            <FiChevronRight size={18} style={{ color: '#C42126' }} />
                                          )}
                                          <FiFolder size={20} style={{ color: '#C42126' }} />
                                          <h6 className="mb-0" style={{
                                            color: '#C42126',
                                            fontWeight: '600',
                                            fontSize: '1.1rem',
                                            flex: 1,
                                            margin: 0
                                          }}>
                                            {group.category ? group.category.name : 'Unclassified'}
                                          </h6>
                                          <span className="text-muted small" style={{ fontSize: '0.85rem' }}>
                                            ({group.documents.length} {group.documents.length === 1 ? 'document' : 'documents'})
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                    {isExpanded && group.documents.map((item, docIndex) => {
                                      const fileIconType = getFileIcon(item);
                                      const iconColor = getFileIconColor(fileIconType);
                                      const rowActions = getActions(item, 0);
                                      
                                      // Determine dropdown direction based on position in the group
                                      // First 4 rows open downward, last 4 rows open upward
                                      const getDropdownDirection = (index, total) => {
                                        if (index < 4) {
                                          return 'down';
                                        }
                                        if (index >= total - 4) {
                                          return 'up';
                                        }
                                        return 'down';
                                      };
                                      const dropdownDirection = getDropdownDirection(docIndex, group.documents.length);

                                      // Scroll to dropdown when opened
                                      const scrollToDropdown = (rowElement, direction) => {
                                        if (!rowElement || !tableWrapperRef.current) return;

                                        setTimeout(() => {
                                          const wrapper = tableWrapperRef.current;
                                          if (!wrapper) return;

                                          // Use getBoundingClientRect to get positions relative to viewport
                                          const wrapperRect = wrapper.getBoundingClientRect();
                                          const rowRect = rowElement.getBoundingClientRect();
                                          
                                          // Calculate positions relative to the wrapper
                                          const rowTopRelativeToWrapper = rowRect.top - wrapperRect.top + wrapper.scrollTop;
                                          const rowBottomRelativeToWrapper = rowRect.bottom - wrapperRect.top + wrapper.scrollTop;
                                          
                                          const wrapperScrollTop = wrapper.scrollTop;
                                          const wrapperHeight = wrapper.clientHeight;
                                          const rowHeight = rowElement.offsetHeight;
                                          
                                          // Calculate if dropdown is visible
                                          const dropdownHeight = 200; // Approximate dropdown menu height
                                          const isDropup = direction === 'up';
                                          
                                          if (isDropup) {
                                            // For dropup, ensure there's space above the row
                                            const spaceNeeded = dropdownHeight + 20; // 20px padding
                                            const rowVisibleTop = rowTopRelativeToWrapper - wrapperScrollTop;
                                            
                                            if (rowVisibleTop < spaceNeeded) {
                                              // Scroll up to show the dropdown above the row with smooth animation
                                              const targetScrollTop = rowTopRelativeToWrapper - spaceNeeded;
                                              wrapper.scrollTo({
                                                top: Math.max(0, targetScrollTop),
                                                behavior: 'smooth'
                                              });
                                            }
                                          } else {
                                            // For dropdown, ensure there's space below the row
                                            const spaceNeeded = dropdownHeight + 20; // 20px padding
                                            const rowVisibleBottom = rowBottomRelativeToWrapper - wrapperScrollTop;
                                            const spaceBelow = wrapperHeight - rowVisibleBottom;
                                            
                                            if (spaceBelow < spaceNeeded) {
                                              // Scroll down to show the dropdown below the row with smooth animation
                                              const targetScrollTop = rowBottomRelativeToWrapper + spaceNeeded - wrapperHeight;
                                              wrapper.scrollTo({
                                                top: Math.min(wrapper.scrollHeight - wrapperHeight, Math.max(0, targetScrollTop)),
                                                behavior: 'smooth'
                                              });
                                            }
                                          }
                                        }, 100); // Delay to ensure dropdown is rendered
                                      };

                                      return (
                                        <tr key={item.id} className="table-row">
                                          <td>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                              {getFileIconComponent(item, 20, iconColor)}
                                            </div>
                                          </td>
                                          <td>
                                            <div
                                              onClick={() => {
                                                if (item.type === 'file') {
                                                  handleViewDetails(item);
                                                } else {
                                                  handleNavigateToFile(item);
                                                }
                                              }}
                                              style={{ cursor: 'pointer', color: '#333', fontWeight: '500' }}
                                              className="text-truncate"
                                              title={item.document_title || item.name}
                                            >
                                              {item.document_title || item.name}
                                            </div>
                                          </td>
                                          <td>
                                            <span className="text-muted">{item.category_name || 'N/A'}</span>
                                          </td>
                                          <td>
                                            <span className="text-muted">{item.reference_number || '—'}</span>
                                          </td>
                                          <td>
                                            <span className="text-muted">{item.expiry_date ? formatDateTimeToDDMMMYYYY(item.expiry_date, false) : '—'}</span>
                                          </td>
                                          <td>
                                            <span className="text-muted">
                                              {item.created_at || item.favorited_at ? formatTimeAgo(item.created_at || item.favorited_at) : 'N/A'}
                                            </span>
                                          </td>
                                          <td>
                                            <span className="text-muted">
                                              {item.uploaded_by_name || item.created_by_name || '—'}
                                            </span>
                                          </td>
                                          
                                          <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                                            {rowActions.length > 0 && (
                                              <div className="table-actions">
                                                <Dropdown
                                                  onToggle={(isOpen, event) => {
                                                    if (isOpen) {
                                                      setOpenDropdownId(item.id);
                                                      // Find the row element - try multiple methods
                                                      let rowElement = event?.target?.closest('tr.table-row') || 
                                                                      event?.target?.closest('.table-row') ||
                                                                      event?.currentTarget?.closest('tr.table-row') ||
                                                                      event?.currentTarget?.closest('.table-row');
                                                      
                                                      if (rowElement) {
                                                        scrollToDropdown(rowElement, dropdownDirection);
                                                      }
                                                    } else {
                                                      setOpenDropdownId(null);
                                                    }
                                                  }}
                                                  show={openDropdownId === item.id}
                                                  drop={dropdownDirection}
                                                >
                                                  <Dropdown.Toggle variant="" as="button" className="dropdown-toggle-btn">
                                                    <i className="fas fa-ellipsis-v"></i>
                                                  </Dropdown.Toggle>
                                                  <Dropdown.Menu align="end">
                                                    {rowActions.map((action, actionIndex) => (
                                                      <Dropdown.Item
                                                        key={actionIndex}
                                                        onClick={(e) => {
                                                          action.onClick(item, 0, e);
                                                          setOpenDropdownId(null);
                                                        }}
                                                        className={action.className || ''}
                                                      >
                                                        {action.icon && <i className={`${action.icon} me-2`}></i>}
                                                        {action.label}
                                                      </Dropdown.Item>
                                                    ))}
                                                  </Dropdown.Menu>
                                                </Dropdown>
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : filteredGroupedData.length === 0 && filteredFiles.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#888', padding: '3rem 1rem' }}>
                      {searchTerm
                        ? 'No files match your search criteria'
                        : 'No favorite documents available'}
                    </div>
                  ) : filteredFiles.length > 0 ? (
                    <CommonTable
                      columns={columns}
                      data={filteredFiles}
                      actions={getActions}
                      onRowClick={(row, rowIndex, e) => {
                        if (!e.target.closest('.table-actions') && 
                            !e.target.closest('a') && 
                            !e.target.closest('[style*="cursor: pointer"]')) {
                          if (row.type === 'file') {
                            handleViewDetails(row);
                          } else if (row.type === 'folder') {
                            handleNavigateToFile(row);
                          }
                        }
                      }}
                      emptyMessage={
                        searchTerm
                          ? 'No files match your search criteria'
                          : 'No favorite documents available'
                      }
                      emptyStateIcon="fas fa-star"
                      getRowId={(row, index) => row.id || index}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#888', padding: '3rem 1rem' }}>
                      {searchTerm
                        ? 'No files match your search criteria'
                        : 'No favorite documents available'}
                    </div>
                  )}
                </>
              )}
              {/* Loading More Indicator */}
              {loadingMore && (
                <div className="d-flex justify-content-center align-items-center py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading more documents...</span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <BackToTop />
      <InternalFooter />
      <style>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default FavoriteDocuments;

